import { useEffect, useState } from "react"
import { Trash2 } from "lucide-react"
import type { AppShell } from "@/App"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  listCategoryRoles,
  upsertCategoryRole,
  deleteCategoryRole,
  type CategoryRoleRow,
} from "@/lib/api"

const SOLAR_ROLES = [
  { value: "panel", label: "Painel" },
  { value: "inverter", label: "Inversor" },
  { value: "battery", label: "Bateria" },
  { value: "structure", label: "Estrutura de Suporte" },
  { value: "meter", label: "Medidor" },
  { value: "counter", label: "Contador" },
  { value: "charger", label: "Carregador" },
  { value: "backup", label: "Backup" },
  { value: "installation", label: "Instalação" },
  { value: "installation_backup", label: "Instalação Backup" },
  { value: "installation_charger", label: "Instalação Carregador" },
  { value: "battery_controller", label: "Controlador Bateria" },
  { value: "labour", label: "Mão de Obra" },
  { value: "accessory", label: "Acessórios" },
]

const CONDITIONAL_FLAGS: Array<{ value: string; label: string }> = [
  { value: "__none__", label: "—" },
  { value: "has_battery", label: "Bateria activa" },
  { value: "has_backup", label: "Backup activo" },
  { value: "has_charger", label: "Carregador activo" },
  { value: "has_meter", label: "Contador activo" },
]

interface EditState {
  role: string
  is_required_in_kit: boolean
  conditional_flag: string
  sort_order: string
  dirty: boolean
  saving: boolean
}

function toEditState(row: CategoryRoleRow): EditState {
  return {
    role: row.role ?? "",
    is_required_in_kit: !!row.is_required_in_kit,
    conditional_flag: row.conditional_flag ?? "__none__",
    sort_order: String(row.sort_order ?? 0),
    dirty: false,
    saving: false,
  }
}

interface CategoryRolesTabProps {
  shell: AppShell | null
}

export function CategoryRolesTab({ shell }: CategoryRolesTabProps) {
  const [rows, setRows] = useState<CategoryRoleRow[]>([])
  const [edits, setEdits] = useState<Record<number, EditState>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function reload() {
    if (!shell) return
    setLoading(true)
    setError(null)
    try {
      const items = await listCategoryRoles(shell)
      setRows(items)
      setEdits(
        Object.fromEntries(items.map((r) => [r.category_id, toEditState(r)])),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha a carregar")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell])

  function patch(categoryId: number, p: Partial<EditState>) {
    setEdits((prev) => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], ...p, dirty: true },
    }))
  }

  async function save(categoryId: number) {
    if (!shell) return
    const state = edits[categoryId]
    if (!state) return
    if (!state.role) {
      shell.bridge.toast({ variant: "error", description: "Role obrigatório" })
      return
    }
    setEdits((prev) => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], saving: true },
    }))
    try {
      await upsertCategoryRole(shell, {
        category_id: categoryId,
        role: state.role,
        is_required_in_kit: state.is_required_in_kit,
        conditional_flag:
          state.conditional_flag === "__none__" ? null : state.conditional_flag,
        sort_order: Number.parseInt(state.sort_order, 10) || 0,
      })
      shell.bridge.toast({
        variant: "success",
        description: "Mapeamento guardado",
      })
      await reload()
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Falha a guardar"
      shell.bridge.toast({ variant: "error", description: msg })
      setEdits((prev) => ({
        ...prev,
        [categoryId]: { ...prev[categoryId], saving: false },
      }))
    }
  }

  async function remove(categoryId: number) {
    if (!shell) return
    try {
      await deleteCategoryRole(shell, categoryId)
      shell.bridge.toast({
        variant: "success",
        description: "Mapeamento removido",
      })
      await reload()
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Falha a remover"
      shell.bridge.toast({ variant: "error", description: msg })
    }
  }

  if (loading)
    return <p className="text-sm text-muted-foreground">A carregar categorias…</p>
  if (error) return <p className="text-sm text-destructive">{error}</p>

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Mapeia categorias do catálogo do CRM ao papel solar usado pelo motor de
        cálculo. Categorias sem role não são consideradas pelo kit.
      </p>
      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Categoria</TableHead>
              <TableHead className="w-44">Role solar</TableHead>
              <TableHead className="w-24">Required</TableHead>
              <TableHead className="w-40">Conditional</TableHead>
              <TableHead className="w-20">Ordem</TableHead>
              <TableHead className="w-32 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const state = edits[row.category_id]
              if (!state) return null
              return (
                <TableRow key={row.category_id}>
                  <TableCell className="font-medium">
                    {row.category_name}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={state.role}
                      onValueChange={(v) => patch(row.category_id, { role: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {SOLAR_ROLES.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={state.is_required_in_kit}
                      onCheckedChange={(v) =>
                        patch(row.category_id, { is_required_in_kit: v })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={state.conditional_flag}
                      onValueChange={(v) =>
                        patch(row.category_id, { conditional_flag: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITIONAL_FLAGS.map((f) => (
                          <SelectItem key={f.value} value={f.value}>
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={state.sort_order}
                      onChange={(e) =>
                        patch(row.category_id, { sort_order: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell className="flex justify-end gap-1">
                    {row.role && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(row.category_id)}
                        title="Remover mapeamento"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant={state.dirty ? "default" : "outline"}
                      disabled={!state.dirty || state.saving}
                      onClick={() => save(row.category_id)}
                    >
                      {state.saving ? "A guardar…" : "Guardar"}
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
